import geohash from 'ngeohash'

import LocationSvg from '@/assets/food/icons/location.svg?react'
import ArrowDownSvg from '@/assets/icons/chevron-down.svg?react'
import CloseSvg from '@/assets/icons/close.svg?react'
import SearchSvg from '@/assets/icons/search.svg?react'
import StoreCardPlaceholder from '@/components/StoreCard/StoreCardPlaceholder'
import StoreCardGrubhub from '@/components/StoreCardGrubhub/StoreCardGrubhub'
import useNumberOfItemsPerRow from '@/hooks/useNumberOfItemsPerRow'
import Pagination from '@/layout/Pagination'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import useGrubhubRestaurantListing from '@/lib/food-grubhub/hooks/useGrubhubRestaurantListing'
// import useGrubhubValidToken from '@/lib/food-grubhub/hooks/useGrubhubValidToken'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {FoodGrubhubRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useSearch from '@/utils/router/useSearch'
import FoodGrubhubInput from '@/views/FoodGrubhub/components/FoodGrubhubSearchWithAutoComplete/FoodGrubhubInput'
import RestaurantSorter, {
  SortBy,
} from '@/views/FoodGrubhub/components/RestaurantSorter/RestaurantSorter'

import style from './FoodGrubhubSearchResult.module.scss'

const FoodGrubhubSearchResultPagePlaceholder = memo(function FoodSearchResultPagePlaceholder() {
  const numberOfItemsPerRow = useNumberOfItemsPerRow()

  const dummy = new Array(numberOfItemsPerRow).fill(undefined)

  return (
    <div className={style.PageSearchResultContainer}>
      {dummy.map((_, idx) => (
        <div key={`store-placeholder-item-${idx.toString()}`}>
          <StoreCardPlaceholder />
        </div>
      ))}
    </div>
  )
})

const EmptySearchResult = () => (
  <div className={style.PageSearchResultEmpty} role='alert'>
    Some results may have changed or been removed. Try refreshing or updating your search.
  </div>
)

export default memo(function FoodGrubhubSearchResult() {
  const router = useRouter()
  // const {lng, lat, address} = FoodGrubhubRoute.useSearch()
  const [storageUserLocation] = useGrubhubUserAddress()
  const lng = storageUserLocation.longitude ?? 0
  const lat = storageUserLocation.latitude ?? 0
  const address = storageUserLocation.address ?? ''
  const prevLatLng = useRef({lat, lng})
  const isFirstRender = useRef(true)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [isNewAddress, setIsNewAddress] = useState(false)
  const [filterInput, setFilterInput] = useState('')
  const [sorter, setSorter] = useState<SortBy>(SortBy.Recommend)
  const [isOpenNow, setIsOpenNow] = useState(true)
  const [isDeliveryUnder30Min, setIsDeliveryUnder30Min] = useState(false)

  const [page, setPage] = useSearch(FoodGrubhubRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    },
  })

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    const locationChanged = prevLatLng.current.lat !== lat || prevLatLng.current.lng !== lng

    if (locationChanged) {
      setPage(1)
      prevLatLng.current = {lat, lng}
    }
  }, [lat, lng])

  const geoGetHashed = useMemo(() => geohash.encode(lat, lng, 12), [lat, lng])

  // const storageToken = useGrubhubValidToken()

  const {data: authData} = useGrubhubGetAuth()

  const restaurantQuery = useMemo(
    () => ({
      orderMethod: 'delivery',
      locationMode: 'DELIVERY',
      facetSet: 'umamiV6',
      pageSize: 36,
      hideHateos: true,
      searchMetrics: true,
      queryText: filterInput,
      location: `POINT(${lng} ${lat})`,
      preciseLocation: true,
      geohash: geoGetHashed,
      includeOffers: true,
      featureControl: 'fastTagBadges:Atrue',
      sortSetId: 'umamiv3',
      sponsoredSize: 5,
      countOmittingTimes: true,
      pageNum: page,
      sorts: sorter,
      facet: [`open_now:${isOpenNow}`, isDeliveryUnder30Min ? 'delivery_time:30' : ''],
    }),
    [filterInput, geoGetHashed, lat, lng, page, sorter, isDeliveryUnder30Min, isOpenNow],
  )

  const {
    data: restaurantResponse,
    isLoading: getRestaurantLoading,
    isFetching,
  } = useGrubhubRestaurantListing(restaurantQuery, authData?.session_handle.access_token ?? '')

  const hasStoreResults = restaurantResponse && restaurantResponse.results.length > 0

  const pageCount =
    (restaurantResponse?.results.length === 36
      ? restaurantResponse.pager.total_pages
      : restaurantResponse?.pager.current_page) ?? 1

  ;(function correctPage() {
    if (isFetching || !restaurantResponse) return
    if (page > pageCount) void setPage(pageCount)
  })()

  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [])

  const getHref = useCallback(
    (page: number) => {
      return router.buildLocation({
        to: FoodGrubhubRoute.fullPath,
        search: {
          page,
        },
      }).href
    },
    [router],
  )

  const [inputValue, setInputValue] = useState('')
  const handleFilterByName = () => {
    setFilterInput(inputValue)
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setFilterInput(inputValue)
    }
  }

  return (
    <div className={style.PageContainer}>
      <div className={style.PageContent}>
        <div className={style.PageFilterContainer}>
          <div className={style.PageFilterContent}>
            <div className={style.FilterGroup}>
              <div className={style.FilterList}>
                <button
                  type='button'
                  onClick={() => {
                    setIsOpenNow(true)
                  }}
                  className={clsx(style.FilterItem, isOpenNow && style.FilterItemActive)}
                  aria-pressed={isOpenNow}
                >
                  Open only
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setIsOpenNow(false)
                  }}
                  className={clsx(style.FilterItem, !isOpenNow && style.FilterItemActive)}
                  aria-pressed={!isOpenNow}
                >
                  All restaurants
                </button>
                <button
                  type='button'
                  className={clsx(style.FilterItem, isDeliveryUnder30Min && style.FilterItemActive)}
                  onClick={() => {
                    setIsDeliveryUnder30Min(prev => !prev)
                  }}
                  aria-pressed={isDeliveryUnder30Min}
                >
                  Under 30 mins
                </button>
              </div>
            </div>
            <div className={style.FilterBreakLine} />
            <div className={clsx(style.FilterGroup, style.Column)}>
              <div className={style.AddressContainer}>
                <VisuallyHidden role='heading' aria-level={1}>
                  Deliver to: {address}
                </VisuallyHidden>
                <p className={style.Heading} aria-hidden='true'>
                  Deliver to:
                </p>
                <div className={style.AddressInfo}>
                  <LocationSvg className={style.AddressInfoIcon} />
                  <div className={style.AddressInfoValue}>
                    <VisuallyHidden>Current address: </VisuallyHidden>
                    {address}
                  </div>
                  <button
                    type='button'
                    aria-label='Change your address'
                    className={style.AddressChangeBtn}
                    onClick={() => {
                      setIsNewAddress(prev => !prev)
                    }}
                  >
                    <ArrowDownSvg
                      className={clsx(
                        style.AddressChangeIcon,
                        isNewAddress && style.AddressChangeIconActive,
                      )}
                    />
                  </button>
                </div>
              </div>
              {isNewAddress && (
                <div className={style.BoxAdressChange}>
                  <FoodGrubhubInput setResetInput={setIsNewAddress} />
                </div>
              )}
              <div className={style.SearchContainer}>
                <div className={style.SearchForm}>
                  <SearchSvg className={style.SearchFormIcon} />
                  <input
                    ref={searchInputRef}
                    value={inputValue}
                    onChange={e => {
                      setInputValue(e.target.value)
                    }}
                    onKeyDown={handleKeyDown}
                    type='text'
                    placeholder='e.g. Pizza, Walmart, Coca Cola'
                    aria-label='Search for food, grocery, or anything'
                    className={style.SearchInput}
                  />
                  {inputValue.length > 0 && (
                    <button
                      type='button'
                      className={style.ClearButton}
                      onClick={() => {
                        setInputValue('')
                        setFilterInput('')
                        searchInputRef.current?.focus()
                      }}
                      aria-label='Clear search input'
                    >
                      <CloseSvg className={style.ClearButtonIcon} />
                    </button>
                  )}
                </div>
                <button
                  type='button'
                  aria-label='Search by name'
                  onClick={handleFilterByName}
                  className={style.SearchButton}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
          <div className={style.PageFilterSort}>
            <div aria-hidden='true' />
            <RestaurantSorter sorter={sorter} setSorter={setSorter} />
          </div>
        </div>
        {getRestaurantLoading ? (
          <FoodGrubhubSearchResultPagePlaceholder />
        ) : (
          <>
            {hasStoreResults ? (
              <>
                <div role='listitem' className={style.PageSearchResultContainer}>
                  {restaurantResponse.results.map(store => {
                    return (
                      <StoreCardGrubhub
                        key={`store-${store.restaurant_id}`}
                        store={store}
                        headingLevel={2}
                      />
                    )
                  })}
                </div>

                {pageCount > 1 && (
                  <Pagination
                    onPageChange={handlePageChange}
                    pageCount={pageCount}
                    hrefBuilder={getHref}
                    currentPage={page - 1}
                  />
                )}
              </>
            ) : (
              <>
                <EmptySearchResult />
                {pageCount > 1 && (
                  <Pagination
                    onPageChange={handlePageChange}
                    pageCount={pageCount}
                    hrefBuilder={getHref}
                    currentPage={page - 1}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
})
